import React, { useEffect, useState } from "react";
import { Button, CardFooter, Spinner, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  participateContest,
  fetchSchoolContests,
} from "store/actions/sportContestAction";
import ReuseableTable from "components/Tables/ReuseableTable";
import Tabs from "components/Tabs";
import ConfirmationModal from "components/modals/ConfirmationModal";
import { fetchAthleteSchool } from "store/actions/athleteActions";
import SportContestLeaderboard from "components/modals/SportContestLeaderboard";
import SportContestDetails from "./SportContestDetails";
import HonorBadge from "components/HonorBadge";

const tabs = [
  { id: "allContests", title: "All Contests" },
  { id: "completedContests", title: "Completed Contests" },
];

const SportContests2 = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { athleteSchool } = useSelector((state) => state.athlete);
  const { sportContests, lastVisible, hasMore } = useSelector(
    (state) => state.contest
  );

  const [activeTab, setActiveTab] = useState(tabs[0]?.id);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState(false);
  const [leaderboardModal, setLeaderboardModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeContest, setActiveContest] = useState(null);
  const [schoolId, setSchoolId] = useState(null);

  const [detailsModal, setDetailsModal] = useState(false);
  const [activeContestId, setActiveContestId] = useState(null);

  const [honorBadgeModal, setHonorBadgeModal] = useState(false);

  const honorBadgeModalToggler = () => {
    setHonorBadgeModal(!honorBadgeModal);
  };

  const columns = ["Contest Id", "Start Date", "End Date", "Your Status"];
  const keyMap = {
    "Contest Id": "contestID",
    "Start Date": "startDate",
    "End Date": "endDate",
    "Your Status": "participated",
  };

  const formateDate = (dateInput) => {
    if (!dateInput) return "";

    let jsDate;

    if (dateInput?.seconds) {
      jsDate = new Date(
        dateInput.seconds * 1000 + (dateInput.nanoseconds || 0) / 1000000
      );
    } else if (dateInput instanceof Date) {
      jsDate = dateInput;
    } else if (typeof dateInput === "string") {
      jsDate = new Date(dateInput);
    }

    if (!jsDate || isNaN(jsDate)) return "";

    return jsDate
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .split("/")
      .join("-");
  };

  const SportContests = sportContests?.map((contest) => ({
    ...contest,
    startDate: formateDate(contest?.startDate),
    endDate: formateDate(contest?.endDate),
  }));

  const detailsModalToggler = () => {
    if (detailsModal) {
      setActiveContestId(null);
    }
    setDetailsModal(!detailsModal);
  };

  const confirmationModalToggler = () => {
    if (confirmationModal) {
      setActiveContest(null);
    }
    setConfirmationModal(!confirmationModal);
  };

  const leaderboardModalToggler = () => {
    if (leaderboardModal) {
      setActiveContest(null);
    }
    setLeaderboardModal(!leaderboardModal);
  };

  const handleParticipate = () => {
    const payload = {
      athleteId: user?.id,
      athleteID: user?.athleteID,
      firstName: user?.first_name,
      lastName: user?.last_name,
      email: user?.email,
      schoolId: athleteSchool?.id || null,
      contestId: activeContest?.id,
      status: "pending",
    };
    setIsUpdating(true);
    dispatch(
      participateContest({
        payload,
        onSuccess: () => confirmationModalToggler(),
      })
    ).finally(() => setIsUpdating(false));
  };

  const handleLoadMore = () => {
    setIsLoading(true);
    dispatch(
      fetchSchoolContests({
        athleteId: user?.id,
        schoolId,
        status:
          activeTab === tabs[0]?.id
            ? ["accepted"]
            : ["admin action pending", "completed", "rewarded"],
        sport: user?.sport.toLowerCase(),
        lastVisible,
      })
    ).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsFetching(true);
    dispatch(
      fetchAthleteSchool({
        city: user?.city || "",
        state: user?.state || "",
        schoolName: user?.institute_name || "",
      })
    )
      .then((res) => {
        if (res) {
          setSchoolId(res?.id);
          dispatch(
            fetchSchoolContests({
              athleteId: user?.id,
              schoolId: res?.id,
              status:
                activeTab === tabs[0]?.id
                  ? ["accepted"]
                  : ["admin action pending", "completed", "rewarded"],
              sport: user?.sport.toLowerCase(),
            })
          ).finally(() => setIsFetching(false));
        } else {
          setIsFetching(false);
        }
      })
      .catch(() => setIsFetching(false));
  }, [activeTab, user?.id]);

  const renderActions = (contest) => (
    <div className="whitespace-nowrap">
      <Button
        color="primary"
        size="sm"
        onClick={() => {
          setActiveContestId(contest?.id);
          detailsModalToggler();
        }}
      >
        View Details
      </Button>
      {activeTab === tabs[0]?.id && (
        <Button
          color="primary"
          size="sm"
          onClick={() => {
            setActiveContest(contest);
            confirmationModalToggler();
          }}
          disabled={contest?.participated === "participated"}
        >
          Participate
        </Button>
      )}
      {activeTab === tabs[1]?.id && (
        <>
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              setActiveContest(contest);
              leaderboardModalToggler();
            }}
          >
            Leaderboard
          </Button>

          <Button
            color="success"
            size="sm"
            className="ml-2"
            onClick={() => {
              setActiveContest(contest);
              honorBadgeModalToggler();
            }}
          >
            Honor Badge
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Row>
      <div className="col">
        <Tabs
          activeTab={activeTab}
          toggleTab={(tab) => setActiveTab(tab)}
          tabs={tabs.map((tab) => ({
            ...tab,
            content: isFetching ? (
              <Spinner color="primary" className="d-flex mx-auto my-4" />
            ) : (
              <ReuseableTable
                columns={columns}
                data={SportContests}
                keyMap={keyMap}
                renderActions={renderActions}
              />
            ),
          }))}
        />
        {hasMore && sportContests?.length > 4 && (
          <CardFooter className="text-center">
            <Button
              color="primary"
              onClick={handleLoadMore}
              style={{ width: "120px" }}
              disabled={isFetching || isLoading}
            >
              {isLoading ? <Spinner size="sm" /> : "Load More"}
            </Button>
          </CardFooter>
        )}
      </div>
      <SportContestDetails
        showModal={detailsModal}
        handleModalToggle={detailsModalToggler}
        contestId={activeContestId}
      />
      <SportContestLeaderboard
        contest={activeContest}
        showModal={leaderboardModal}
        handleModalToggle={leaderboardModalToggler}
      />
      <ConfirmationModal
        isOpen={confirmationModal}
        toggle={confirmationModalToggler}
        onConfirm={handleParticipate}
        isLoading={isUpdating}
        title="Participate Contest"
        message="Are you sure you want to participate in this contest?"
      />
      <HonorBadge
        toggle={honorBadgeModalToggler}
        addModal={honorBadgeModal}
        Id={activeContest?.id}
        winnerQrCode={null}
      />
    </Row>
  );
};

export default SportContests2;
